// notifyOnPlanChange — Ship 6 of canvas-weekly-commitments (May 13, 2026)
//
// Fires when a rep submits a "Plan changed?" request from the home Commitment
// Card. Notifies their up-chain managers (TL → ASM, plus any non-null fallback)
// via an in-portal `notifications` row AND a Twilio SMS so they see it on their
// phone within seconds.
//
// Routing chain (resolved at call time, NOT at request-creation time, so it
// uses the CURRENT team graph — handles mid-week reassignments cleanly):
//   • Rep's `team_roster.team_lead_id` (the TL)
//   • Rep's `team_roster.asm_id`       (the ASM)
//   • Both are notified if both are set. Duplicate user_ids deduped.
//   • If neither is set (loose-end RM or canvasser), falls back to all active
//     sales_director users so the request doesn't fall on the floor.
//
// Fire-and-forget: any failure is logged but never blocks the rep's UI.
// Mirrors notifyChadOnBuildApproval (Apr 29) pattern verbatim.

import { supabase } from "./supabase";

const PORTAL_BASE_URL = "https://app.bardinroofing.com";

interface PlanChangeNotifyInput {
  changeRequestId: string;
  repId: string;
  repName: string;
  reason: string | null;
  note: string | null;
  blocksChangedCount: number;
}

function toE164(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

async function resolveRecipients(repId: string): Promise<Array<{ id: string; full_name: string; phone: string | null }>> {
  // Pull rep's TL/ASM ids
  const { data: rep } = await supabase
    .from("team_roster")
    .select("team_lead_id,asm_id")
    .eq("id", repId)
    .maybeSingle();

  const ids = new Set<string>();
  if (rep?.team_lead_id) ids.add(rep.team_lead_id);
  if (rep?.asm_id)       ids.add(rep.asm_id);

  if (ids.size > 0) {
    const { data } = await supabase
      .from("team_roster")
      .select("id,full_name,phone")
      .in("id", Array.from(ids))
      .eq("is_active", true);
    return (data ?? []) as Array<{ id: string; full_name: string; phone: string | null }>;
  }

  // Fallback: notify all active sales_directors
  const { data: dirs } = await supabase
    .from("team_roster")
    .select("id,full_name,phone")
    .eq("role", "sales_director")
    .eq("is_active", true);
  return (dirs ?? []) as Array<{ id: string; full_name: string; phone: string | null }>;
}

export async function notifyOnPlanChange(input: PlanChangeNotifyInput): Promise<void> {
  try {
    const recipients = await resolveRecipients(input.repId);
    if (recipients.length === 0) {
      console.warn("[notifyOnPlanChange] no recipients resolved for rep", input.repId);
      return;
    }

    const reasonLabel = input.reason ? input.reason.charAt(0).toUpperCase() + input.reason.slice(1) : "Not specified";
    const auditUrl = `${PORTAL_BASE_URL}/canvas/manager-audit?mode=oneone&rep=${encodeURIComponent(input.repId)}`;
    const blocksWord = input.blocksChangedCount === 1 ? "1 block" : `${input.blocksChangedCount} blocks`;
    const titleBase = `Plan change request — ${input.repName}`;
    const bodyBase = `${blocksWord} · Reason: ${reasonLabel}${input.note ? ` · ${input.note}` : ""}`;
    const smsBody = `${input.repName} (RM) requested a plan change — ${blocksWord}. Reason: ${reasonLabel}. Open: ${auditUrl}`;

    const inApp = recipients.map((r) =>
      supabase.from("notifications").insert({
        user_id: r.id,
        type: "commitment_change_request",
        title: titleBase,
        body: bodyBase,
        metadata: { url: auditUrl, change_request_id: input.changeRequestId, rep_id: input.repId },
      })
    );

    const sms = recipients.map((r) => {
      const phone = toE164(r.phone);
      if (!phone) return Promise.resolve(null);
      return supabase.functions.invoke("twilio-send-sms", {
        body: { to: phone, body: smsBody, tag: "commitment_change_request" },
      });
    });

    await Promise.allSettled([...inApp, ...sms]);
  } catch (err) {
    console.warn("[notifyOnPlanChange] unexpected error:", err);
  }
}

/**
 * When a manager Accepts or Rejects a plan-change request, fire an in-app
 * notification + SMS back to the REP so they know what happened.
 */
export async function notifyRepOnPlanChangeResolved(input: {
  changeRequestId: string;
  repId: string;
  resolution: "accepted" | "rejected";
  resolverName: string;
  resolverNote?: string | null;
}): Promise<void> {
  try {
    const { data: rep } = await supabase
      .from("team_roster")
      .select("id,full_name,phone")
      .eq("id", input.repId)
      .maybeSingle();
    if (!rep) return;

    const verb = input.resolution === "accepted" ? "approved" : "declined";
    const title = `Your plan-change request was ${verb}`;
    const body = input.resolution === "accepted"
      ? `${input.resolverName} approved your plan change.${input.resolverNote ? ` "${input.resolverNote}"` : ""}`
      : `${input.resolverName} didn't approve the plan change.${input.resolverNote ? ` "${input.resolverNote}"` : " Talk to them about next steps."}`;
    const homeUrl = `${PORTAL_BASE_URL}/`;
    const smsBody = `${title}. ${body} ${homeUrl}`;

    const inApp = supabase.from("notifications").insert({
      user_id: rep.id,
      type: "commitment_change_resolved",
      title,
      body,
      metadata: { url: homeUrl, change_request_id: input.changeRequestId, resolution: input.resolution },
    });

    const phone = toE164(rep.phone);
    const sms = phone
      ? supabase.functions.invoke("twilio-send-sms", {
          body: { to: phone, body: smsBody, tag: "commitment_change_resolved" },
        })
      : Promise.resolve(null);

    await Promise.allSettled([inApp, sms]);
  } catch (err) {
    console.warn("[notifyRepOnPlanChangeResolved] unexpected error:", err);
  }
}
